// ============================================================================
// ProfilePhotoCrop.jsx — Profile photo upload with dual crop (rect + round)
// ============================================================================
// Uses react-easy-crop for pan/zoom cropping.
// Produces two outputs: rectangular (passport-style) and round (avatar).
// Uploads cropped images to Supabase Storage bucket 'profile-photos'.
// Stores storage paths (not base64) in user_profiles fields.
// ============================================================================

import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { X, ZoomIn, ZoomOut, Camera, Check, ChevronRight, Loader2 } from 'lucide-react';
import { supabase } from '../supabaseClient';

const FONT = "'Raleway', sans-serif";
const BRIO_GOLD = '#9f902a';
const BRIO_GOLD_LIGHT = 'rgba(159, 144, 42, 0.1)';
const BRIO_BORDER = '#e5e5e5';

// Portrait crop aspect ratio (270×380)
const RECT_ASPECT = 270 / 380;
const ROUND_ASPECT = 1;

const RECT_OUTPUT_WIDTH = 270;
const RECT_OUTPUT_HEIGHT = 380;
const ROUND_OUTPUT_SIZE = 400;

// Explicit round crop diameter in the UI (fits inside 360px-tall container)
const ROUND_CROP_DIAMETER = 280;
// Inner dashed guide is 10% smaller than the crop outline
const ROUND_GUIDE_DIAMETER = Math.round(ROUND_CROP_DIAMETER * 0.9);
const BUCKET = 'profile-photos';
const SIGNED_URL_EXPIRY = 3600; // 1 hour

/**
 * Ensure the storage bucket exists, creating it if needed.
 * Called once before first upload attempt.
 */
let bucketEnsured = false;
async function ensureBucket() {
  if (bucketEnsured) return;
  try {
    const { error } = await supabase.storage.createBucket(BUCKET, {
      public: false,
      fileSizeLimit: 5242880, // 5MB
      allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    });
    // 'already exists' is fine — any other error we log but don't block
    if (error && !error.message?.includes('already exists')) {
      console.warn('[ProfilePhotoCrop] bucket creation note:', error.message);
    }
  } catch (err) {
    console.warn('[ProfilePhotoCrop] bucket check:', err.message);
  }
  bucketEnsured = true;
}

/**
 * Compute output canvas dimensions for the crop.
 * Rectangular: always 270×380. Round: always ROUND_OUTPUT_SIZE × ROUND_OUTPUT_SIZE.
 */
function getOutputDimensions(circular) {
  if (circular) return { w: ROUND_OUTPUT_SIZE, h: ROUND_OUTPUT_SIZE };
  return { w: RECT_OUTPUT_WIDTH, h: RECT_OUTPUT_HEIGHT };
}

/**
 * Create a cropped canvas and return a Blob (JPEG).
 */
function getCroppedBlob(imageSrc, pixelCrop, circular = false) {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const { w, h } = getOutputDimensions(circular);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');

      if (circular) {
        ctx.beginPath();
        ctx.arc(w / 2, h / 2, w / 2, 0, Math.PI * 2);
        ctx.clip();
      }

      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y,
        pixelCrop.width, pixelCrop.height,
        0, 0,
        w, h,
      );
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85);
    };
    image.src = imageSrc;
  });
}

/**
 * Also produce a data URL for local preview before upload.
 */
function getCroppedDataUrl(imageSrc, pixelCrop, circular = false) {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const { w, h } = getOutputDimensions(circular);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');

      if (circular) {
        ctx.beginPath();
        ctx.arc(w / 2, h / 2, w / 2, 0, Math.PI * 2);
        ctx.clip();
      }

      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y,
        pixelCrop.width, pixelCrop.height,
        0, 0,
        w, h,
      );
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    image.src = imageSrc;
  });
}

/**
 * Upload a blob to Supabase Storage.
 * Returns the storage path on success.
 */
async function uploadToStorage(userId, variant, blob) {
  await ensureBucket();
  const ext = 'jpg';
  const path = `${userId}/${variant}.${ext}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, {
      contentType: 'image/jpeg',
      upsert: true,
    });
  if (error) throw error;
  return path;
}

/**
 * Get a signed URL for a storage path.
 * Returns the URL string or null if path is empty/invalid.
 */
export async function getProfilePhotoSignedUrl(storagePath) {
  if (!storagePath || storagePath.startsWith('data:')) return storagePath; // backward compat with base64
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(storagePath, SIGNED_URL_EXPIRY);
    if (error) throw error;
    return data.signedUrl;
  } catch {
    return null;
  }
}

/**
 * Hook to resolve a storage path to a signed URL.
 * Refreshes when the path changes.
 */
export function useProfilePhotoUrl(storagePath) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!storagePath) { setUrl(null); return; }
    // Backward compat: if it's already a data URL, use directly
    if (storagePath.startsWith('data:')) { setUrl(storagePath); return; }
    let cancelled = false;
    getProfilePhotoSignedUrl(storagePath).then(u => {
      if (!cancelled) setUrl(u);
    });
    return () => { cancelled = true; };
  }, [storagePath]);

  return url;
}

/**
 * Hook to resolve multiple storage paths to signed URLs (batch).
 * Input: object { [userId]: storagePath }
 * Returns: object { [userId]: signedUrl }
 */
export function useProfilePhotoUrls(pathMap) {
  const [urls, setUrls] = useState({});

  useEffect(() => {
    if (!pathMap || Object.keys(pathMap).length === 0) { setUrls({}); return; }
    let cancelled = false;

    const entries = Object.entries(pathMap).filter(([, p]) => p);
    if (entries.length === 0) { setUrls({}); return; }

    Promise.all(
      entries.map(async ([userId, path]) => {
        const url = await getProfilePhotoSignedUrl(path);
        return [userId, url];
      })
    ).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(([uid, url]) => { if (url) map[uid] = url; });
      setUrls(map);
    });

    return () => { cancelled = true; };
  }, [pathMap]);

  return urls;
}

export default function ProfilePhotoCrop({
  userId,
  currentPhotoRect,
  currentPhotoRound,
  onSave,
  disabled = false,
}) {
  const [step, setStep] = useState(null); // null | 'rect' | 'round' | 'preview'
  const [imageSrc, setImageSrc] = useState(null);
  const [rectCrop, setRectCrop] = useState({ x: 0, y: 0 });
  const [rectZoom, setRectZoom] = useState(1);
  const [rectCroppedArea, setRectCroppedArea] = useState(null);
  const [roundCrop, setRoundCrop] = useState({ x: 0, y: 0 });
  const [roundZoom, setRoundZoom] = useState(1);
  const [roundCroppedArea, setRoundCroppedArea] = useState(null);
  const [rectPreview, setRectPreview] = useState(null);   // data URL for preview
  const [roundPreview, setRoundPreview] = useState(null);  // data URL for preview
  const [rectBlob, setRectBlob] = useState(null);          // Blob for upload
  const [roundBlob, setRoundBlob] = useState(null);        // Blob for upload
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const fileInputRef = useRef(null);

  // Resolve existing storage paths to signed URLs for display
  const rectUrl = useProfilePhotoUrl(currentPhotoRect);
  const roundUrl = useProfilePhotoUrl(currentPhotoRound);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setStep('rect');
      setRectCrop({ x: 0, y: 0 });
      setRectZoom(1);
      setRoundCrop({ x: 0, y: 0 });
      setRoundZoom(1);
      setRectPreview(null);
      setRoundPreview(null);
      setRectBlob(null);
      setRoundBlob(null);
      setSaveError('');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const handleRectCropComplete = useCallback((_, croppedAreaPixels) => {
    setRectCroppedArea(croppedAreaPixels);
  }, []);

  const handleRoundCropComplete = useCallback((_, croppedAreaPixels) => {
    setRoundCroppedArea(croppedAreaPixels);
  }, []);

  const confirmRect = async () => {
    if (!rectCroppedArea) return;
    const [preview, blob] = await Promise.all([
      getCroppedDataUrl(imageSrc, rectCroppedArea, false),
      getCroppedBlob(imageSrc, rectCroppedArea, false),
    ]);
    setRectPreview(preview);
    setRectBlob(blob);
    setStep('round');
  };

  const confirmRound = async () => {
    if (!roundCroppedArea) return;
    const [preview, blob] = await Promise.all([
      getCroppedDataUrl(imageSrc, roundCroppedArea, true),
      getCroppedBlob(imageSrc, roundCroppedArea, true),
    ]);
    setRoundPreview(preview);
    setRoundBlob(blob);
    setStep('preview');
  };

  const handleSave = async () => {
    if (!userId || !rectBlob || !roundBlob) return;
    setSaving(true);
    setSaveError('');
    try {
      // Upload original (from File input as blob)
      const originalBlob = await fetch(imageSrc).then(r => r.blob());
      const [originalPath, rectPath, roundPath] = await Promise.all([
        uploadToStorage(userId, 'original', originalBlob),
        uploadToStorage(userId, 'rect', rectBlob),
        uploadToStorage(userId, 'round', roundBlob),
      ]);
      await onSave({
        profilePhotoOriginal: originalPath,
        profilePhotoRect: rectPath,
        profilePhotoRound: roundPath,
      });
      setStep(null);
      setImageSrc(null);
    } catch (err) {
      console.error('[ProfilePhotoCrop] upload error:', err);
      setSaveError(err.message || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setStep(null);
    setImageSrc(null);
    setRectPreview(null);
    setRoundPreview(null);
    setRectBlob(null);
    setRoundBlob(null);
    setSaveError('');
  };

  const hasPhotos = rectUrl || roundUrl;

  return (
    <div style={{ fontFamily: FONT }}>
      {/* Current photos + upload button */}
      {!step && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {hasPhotos && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {rectUrl && (
                <img
                  src={rectUrl}
                  alt="Pass photo"
                  style={{
                    width: '42px',
                    height: '59px',
                    objectFit: 'cover',
                    border: `1px solid ${BRIO_BORDER}`,
                  }}
                />
              )}
              {roundUrl && (
                <img
                  src={roundUrl}
                  alt="Avatar"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `1px solid ${BRIO_BORDER}`,
                  }}
                />
              )}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {!disabled && (
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '6px 12px',
                background: BRIO_GOLD_LIGHT,
                color: BRIO_GOLD,
                border: `1px solid ${BRIO_GOLD}`,
                borderRadius: 0,
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: FONT,
              }}
            >
              <Camera size={13} />
              {hasPhotos ? 'Change Photo' : 'Upload Photo'}
            </button>
          )}
        </div>
      )}

      {/* Crop Modal Overlay */}
      {step && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: 'white',
              width: '480px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: FONT,
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: `1px solid ${BRIO_BORDER}`,
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
                {step === 'rect' && 'Step 1: Crop Pass Photo'}
                {step === 'round' && 'Step 2: Crop Avatar'}
                {step === 'preview' && 'Preview & Save'}
              </span>
              <button onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <X size={18} color="#6b7280" />
              </button>
            </div>

            {/* Cropper area */}
            {(step === 'rect' || step === 'round') && (
              <div style={{ position: 'relative', height: '360px', background: '#1a1a2e' }}>
                <Cropper
                  image={imageSrc}
                  crop={step === 'rect' ? rectCrop : roundCrop}
                  zoom={step === 'rect' ? rectZoom : roundZoom}
                  aspect={step === 'rect' ? RECT_ASPECT : ROUND_ASPECT}
                  cropShape={step === 'round' ? 'round' : 'rect'}
                  cropSize={step === 'round' ? { width: ROUND_CROP_DIAMETER, height: ROUND_CROP_DIAMETER } : undefined}
                  onCropChange={step === 'rect' ? setRectCrop : setRoundCrop}
                  onZoomChange={step === 'rect' ? setRectZoom : setRoundZoom}
                  onCropComplete={step === 'rect' ? handleRectCropComplete : handleRoundCropComplete}
                  showGrid={step === 'rect'}
                />
                {/* Inner guide circle — 10% smaller than the crop outline */}
                {step === 'round' && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: ROUND_GUIDE_DIAMETER + 'px',
                      height: ROUND_GUIDE_DIAMETER + 'px',
                      transform: 'translate(-50%, -50%)',
                      border: '1.5px dashed rgba(255,255,255,0.35)',
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      zIndex: 3,
                    }}
                    title="Ideal face positioning"
                  />
                )}
              </div>
            )}

            {/* Zoom controls */}
            {(step === 'rect' || step === 'round') && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '10px 16px',
                  borderBottom: `1px solid ${BRIO_BORDER}`,
                }}
              >
                <ZoomOut size={16} color="#6b7280" />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={step === 'rect' ? rectZoom : roundZoom}
                  onChange={e => {
                    const val = Number(e.target.value);
                    if (step === 'rect') setRectZoom(val);
                    else setRoundZoom(val);
                  }}
                  style={{ width: '200px', accentColor: BRIO_GOLD }}
                />
                <ZoomIn size={16} color="#6b7280" />
              </div>
            )}

            {/* Preview */}
            {step === 'preview' && (
              <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', gap: '24px', alignItems: 'flex-end' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', fontWeight: 500 }}>Pass Photo</div>
                  {rectPreview && (
                    <img
                      src={rectPreview}
                      alt="Rectangular crop"
                      style={{ width: '108px', height: '152px', objectFit: 'cover', border: `1px solid ${BRIO_BORDER}` }}
                    />
                  )}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', fontWeight: 500 }}>Avatar</div>
                  {roundPreview && (
                    <img
                      src={roundPreview}
                      alt="Round crop"
                      style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: `1px solid ${BRIO_BORDER}` }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderTop: `1px solid ${BRIO_BORDER}`,
              }}
            >
              <div>
                {saveError && (
                  <span style={{ fontSize: '11px', color: '#ef4444', fontFamily: FONT }}>{saveError}</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleCancel}
                  style={{
                    padding: '6px 16px',
                    background: 'white',
                    border: `1px solid ${BRIO_BORDER}`,
                    borderRadius: 0,
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: FONT,
                    color: '#374151',
                  }}
                >
                  Cancel
                </button>
                {step === 'rect' && (
                  <button
                    onClick={confirmRect}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 16px',
                      background: BRIO_GOLD,
                      color: 'white',
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: FONT,
                    }}
                  >
                    Next <ChevronRight size={14} />
                  </button>
                )}
                {step === 'round' && (
                  <button
                    onClick={confirmRound}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 16px',
                      background: BRIO_GOLD,
                      color: 'white',
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: FONT,
                    }}
                  >
                    Next <ChevronRight size={14} />
                  </button>
                )}
                {step === 'preview' && (
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 16px',
                      background: BRIO_GOLD,
                      color: 'white',
                      border: 'none',
                      borderRadius: 0,
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: saving ? 'wait' : 'pointer',
                      fontFamily: FONT,
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    {saving ? (
                      <><Loader2 size={14} className="animate-spin" /> Uploading...</>
                    ) : (
                      <><Check size={14} /> Save Photos</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
